import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FaEdit, FaTrash, FaPlus, FaEye } from "react-icons/fa";
import { toast } from "react-toastify";

import { PageWrapper } from "../components/admin";
import { Button } from "../components/form";
import { BaseModal } from "../components/popups";
import { CodeEditor, FullScreenLoader } from "../components/common";

import {
  getEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate
} from "../service";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "de", label: "German" },
  { code: "fr", label: "French" },
  { code: "it", label: "Italian" }
];

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [editTemplate, setEditTemplate] = useState(null);
  const [deleteTemplate, setDeleteTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [activeLanguage, setActiveLanguage] = useState("en");
  const [previewMode, setPreviewMode] = useState(false);
  const { orgId } = useParams();

  const fetchEmailTemplates = async () => {
    try {
      setLoading("Loading email templates...");
      const { data } = await getEmailTemplates(orgId);
      setTemplates(data);
    } catch (error) {
      console.error("Fetch email templates error", error);
      toast.error("Failed to fetch email templates");
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async (template) => {
    try {
      setLoading("Saving email template...");

      if (template._id && template._id.toString().length === 24) {
        await updateEmailTemplate(orgId, template._id, template);
        toast.success("Email template updated successfully");
      } else {
        await createEmailTemplate(template, orgId);
        toast.success("Email template created successfully");
      }

      setEditTemplate(null);
      await fetchEmailTemplates();
    } catch (error) {
      toast.error(error?.response?.data?.message ?? "Failed to save email template");
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteTemplate = async (template) => {
    try {
      setLoading("Deleting email template...");
      await deleteEmailTemplate(orgId, template._id);
      setDeleteTemplate(null);
      toast.success("Email template deleted successfully");
      await fetchEmailTemplates();
    } catch (error) {
      toast.error(error?.response?.data?.message ?? "Failed to delete email template");
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateClick = (templateId) => {
    setExpandedTemplate(expandedTemplate === templateId ? null : templateId);
  };

  const handleEditTemplate = (template) => {
    setEditTemplate({ ...template });
  };

  const handleCreateTemplate = () => {
    const newTemplate = {
      title: "",
      subject_en: "",
      subject_de: "",
      subject_fr: "",
      subject_it: "",
      html_en: "",
      html_de: "",
      html_fr: "",
      html_it: ""
    };
    setEditTemplate(newTemplate);
  };

  const updateEditTemplate = (field, value) => {
    setEditTemplate((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (!orgId) return;
    fetchEmailTemplates();
  }, [orgId]);

  return (
    <div>
      <PageWrapper pageHeading={"Email Templates"}>
        {templates.map((template) => (
          <div key={template._id} className="bg-white shadow-sm mb-4 rounded">
            {/* Accordion Header */}
            <div
              className="flex flex-row justify-between items-center cursor-pointer px-4 py-3 hover:bg-gray-50"
              onClick={() => handleTemplateClick(template._id)}>
              <div className="flex items-center gap-4">
                <h3 className="font-semibold text-gray-800">
                  {template.title || "Untitled Template"}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="p-2 hover:bg-blue-100 rounded"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditTemplate(template);
                  }}>
                  <FaEdit className="text-blue-600" />
                </button>
                <button
                  className="p-2 hover:bg-red-100 rounded"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTemplate(template);
                  }}>
                  <FaTrash className="text-red-600" />
                </button>
              </div>
            </div>

            {/* Accordion Content */}
            {expandedTemplate === template._id && (
              <div className="border-t px-4 py-4">
                {/* Language Tabs */}
                <div className="flex border-b mb-4">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      className={`px-4 py-2 font-medium ${
                        activeLanguage === lang.code
                          ? "border-b-2 border-blue-500 text-blue-600"
                          : "text-gray-600 hover:text-gray-800"
                      }`}
                      onClick={() => setActiveLanguage(lang.code)}>
                      {lang.label}
                    </button>
                  ))}
                </div>

                {/* Content for Active Language */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject ({LANGUAGES.find((l) => l.code === activeLanguage)?.label})
                    </label>
                    <div className="p-3 bg-gray-50 border rounded">
                      {template[`subject_${activeLanguage}`] || "No subject set"}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      HTML Content ({LANGUAGES.find((l) => l.code === activeLanguage)?.label})
                    </label>
                    <div className="border rounded">
                      <div className="flex justify-between items-center p-2 bg-gray-100 border-b">
                        <span className="text-sm font-medium">Preview</span>
                        <button
                          className="text-sm text-blue-600 hover:text-blue-800"
                          onClick={() => setPreviewMode(!previewMode)}>
                          <FaEye className="inline mr-1" />
                          {previewMode ? "Hide" : "Show"} HTML
                        </button>
                      </div>
                      {previewMode ? (
                        <div className="p-3 bg-gray-50 font-mono text-sm max-h-40 overflow-y-auto">
                          {template[`html_${activeLanguage}`] || "No HTML content"}
                        </div>
                      ) : (
                        <div
                          className="p-3 min-h-32 max-h-40 overflow-y-auto"
                          dangerouslySetInnerHTML={{
                            __html: template[`html_${activeLanguage}`] || "<p>No HTML content</p>"
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        <Button.ButtonOutlined
          className="my-3 w-full font-bold flex items-center justify-center gap-2"
          handler={handleCreateTemplate}>
          <FaPlus className="w-4 h-4" />
          Add New Email Template
        </Button.ButtonOutlined>

        {/* Edit/Create Modal */}
        <BaseModal
          open={editTemplate ? true : false}
          setOpen={setEditTemplate}
          modalSize="w-[90%] max-w-4xl"
          title={!editTemplate?._id ? "Create Email Template" : "Edit Email Template"}
          customButtonTitle="Save"
          customButtonAction={() => saveTemplate(editTemplate)}>
          {editTemplate && (
            <div className="space-y-6">
              {/* Template Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Title *
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editTemplate.title || ""}
                  onChange={(e) => updateEditTemplate("title", e.target.value)}
                  placeholder="Enter template title"
                />
              </div>

              {/* Language Tabs for Editing */}
              <div className="flex border-b">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    className={`px-4 py-2 font-medium ${
                      activeLanguage === lang.code
                        ? "border-b-2 border-blue-500 text-blue-600"
                        : "text-gray-600 hover:text-gray-800"
                    }`}
                    onClick={() => setActiveLanguage(lang.code)}>
                    {lang.label}
                  </button>
                ))}
              </div>

              {/* Subject Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject ({LANGUAGES.find((l) => l.code === activeLanguage)?.label})
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editTemplate[`subject_${activeLanguage}`] || ""}
                  onChange={(e) => updateEditTemplate(`subject_${activeLanguage}`, e.target.value)}
                  placeholder={`Enter subject in ${LANGUAGES.find((l) => l.code === activeLanguage)?.label}`}
                />
              </div>

              {/* HTML Content Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  HTML Content ({LANGUAGES.find((l) => l.code === activeLanguage)?.label})
                </label>
                <div className="border rounded p-4 bg-gray-50">
                  <CodeEditor
                    value={editTemplate[`html_${activeLanguage}`] || ""}
                    onChange={(value) => updateEditTemplate(`html_${activeLanguage}`, value)}
                  />
                </div>
              </div>

              {/* Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preview ({LANGUAGES.find((l) => l.code === activeLanguage)?.label})
                </label>
                <div className="border rounded p-4 bg-gray-50 min-h-96 overflow-y-auto">
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        editTemplate[`html_${activeLanguage}`] || "<p>No content to preview</p>"
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </BaseModal>

        {/* Delete Confirmation Modal */}
        <BaseModal
          open={deleteTemplate ? true : false}
          setOpen={setDeleteTemplate}
          title={`Delete Email Template`}>
          {deleteTemplate && (
            <div>
              <p className="text-base text-black mb-4">
                Are you sure you want to delete "{deleteTemplate.title || "Untitled Template"}"?
              </p>
              <p className="text-sm text-gray-600 mb-4">This action cannot be undone.</p>
              <Button.ButtonOutlined
                className="border-red-500 flex items-center justify-center w-full text-red-500 gap-2"
                handler={() => confirmDeleteTemplate(deleteTemplate)}>
                <FaTrash className="w-4 h-4" />
                Delete Template
              </Button.ButtonOutlined>
            </div>
          )}
        </BaseModal>

        {loading && <FullScreenLoader text={loading} />}
      </PageWrapper>
    </div>
  );
}
